import React, { useEffect, useState } from 'react';
import { db, functions } from '../firebase';
import { collection, getDocs, addDoc, doc, updateDoc, Timestamp, query, where } from 'firebase/firestore';
import { Reservation } from '../types/types';
import { Button, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useAutoCheckExpiredReservations } from '../hooks/useExpiredReservations';

interface BookingPeriod {
  id: string;
  name: string;
  start_day: string;
  end_day: string;
  booking_open_at: any;
  booking_close_at: any;
  supplementation_open_at: any;
  mode: string;
  api_url: string;
  api_url2?: string;
  created_at: any;
}

interface LotteryWinner {
  id: string;
  person_name: string;
  person_email: string;
  day: string;
  homeTeam: string;
  awayTeam: string;
  status: string;
  booking_period_id: any;
}

const Admin: React.FC = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [lotteryWinners, setLotteryWinners] = useState<LotteryWinner[]>([]);
  const [bookingPeriods, setBookingPeriods] = useState<BookingPeriod[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<BookingPeriod | null>(null);
  const [newBookingPeriod, setNewBookingPeriod] = useState({
    name: '',
    start_day: '',
    end_day: '',
    booking_open_at: '',
    booking_close_at: '',
    supplementation_open_at: '',
    mode: 'FCFS',
    api_url: '',
    api_url2: '',
    infoText: '',
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Automaattinen tarkistus vanhentuneiden PENDING-varausten avaamiseksi
  const { isChecking, error: expiredError } = useAutoCheckExpiredReservations(true);

  // Muokkaustila
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editName, setEditName] = useState<string>('');
  const [editMode, setEditMode] = useState<string>('');
  const [editStartDay, setEditStartDay] = useState<string>('');
  const [editEndDay, setEditEndDay] = useState<string>('');
  const [editBookingOpenAt, setEditBookingOpenAt] = useState<string>('');
  const [editBookingCloseAt, setEditBookingCloseAt] = useState<string>('');
  const [editApiUrl, setEditApiUrl] = useState<string>('');
  const [editApiUrl2, setEditApiUrl2] = useState<string>('');
  const [editSupplementationOpenAt, setEditSupplementationOpenAt] = useState<string>('');
  const [editInfoText, setEditInfoText] = useState<string>('');

  // Lomakkeen avaus/sulku
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'reservations'));
        const res: Reservation[] = [];
        querySnapshot.forEach((doc) => {
          res.push({ id: doc.id, ...doc.data() } as Reservation);
        });
        setReservations(res);
      } catch (err: any) {
        setError(err.message);
      }
    };

    const fetchLotteryWinners = async () => {
      try {
        const winnersQuery = query(
          collection(db, 'detailed_lottery'),
          where('status', '==', 'WINNER')
        );
        const querySnapshot = await getDocs(winnersQuery);
        const winners: LotteryWinner[] = [];
        querySnapshot.forEach((doc) => {
          winners.push({ id: doc.id, ...doc.data() } as LotteryWinner);
        });
        setLotteryWinners(winners);
      } catch (err: any) {
        setError(err.message);
      }
    };

    const fetchBookingPeriods = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'booking_periods'));
        const bp: BookingPeriod[] = [];
        querySnapshot.forEach((doc) => {
          bp.push({ id: doc.id, ...doc.data() } as BookingPeriod);
        });
        setBookingPeriods(bp);
      } catch (err: any) {
        setError(err.message);
      }
    };

    fetchReservations();
    fetchLotteryWinners();
    fetchBookingPeriods();
  }, []);

  // Näytä virhe vanhentuneiden varausten tarkistuksesta
  useEffect(() => {
    if (expiredError) {
      console.error('Failed to check expired reservations:', expiredError);
    }
  }, [expiredError]);

  const handleAddBookingPeriod = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      // Muunna datetime-local merkkijonot Firestore Timestampeiksi
      const stringToTimestamp = (dateStr: string) => {
        if (!dateStr) return null;
        return Timestamp.fromDate(new Date(dateStr));
      };

      await addDoc(collection(db, 'booking_periods'), {
        name: newBookingPeriod.name,
        start_day: newBookingPeriod.start_day,
        end_day: newBookingPeriod.end_day,
        booking_open_at: stringToTimestamp(newBookingPeriod.booking_open_at),
        booking_close_at: stringToTimestamp(newBookingPeriod.booking_close_at),
        supplementation_open_at: newBookingPeriod.supplementation_open_at ? stringToTimestamp(newBookingPeriod.supplementation_open_at) : null,
        mode: newBookingPeriod.mode,
        api_url: newBookingPeriod.api_url,
        api_url2: newBookingPeriod.api_url2,
        infoText: newBookingPeriod.infoText,
        created_at: new Date(),
      });
      setNewBookingPeriod({
        name: '',
        start_day: '',
        end_day: '',
        booking_open_at: '',
        booking_close_at: '',
        supplementation_open_at: '',
        mode: 'FCFS',
        api_url: '',
        api_url2: '',
        infoText: '',
      });
      // Päivitä booking periodit listaan
      const querySnapshot = await getDocs(collection(db, 'booking_periods'));
      const bp: BookingPeriod[] = [];
      querySnapshot.forEach((doc) => {
        bp.push({ id: doc.id, ...doc.data() } as BookingPeriod);
      });
      setBookingPeriods(bp);
      setSuccess('Uusi varausjakso lisätty onnistuneesti!');
      setIsFormOpen(false); // Sulje lomake onnistumisen jälkeen
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEditPeriod = () => {
    if (!selectedPeriod) return;
    
    setIsEditing(true);
    setEditName(selectedPeriod.name);
    setEditMode(selectedPeriod.mode);
    setEditStartDay(selectedPeriod.start_day);
    setEditEndDay(selectedPeriod.end_day);
    
    // Muunna Timestamp datetime-local inputille sopivaan muotoon (YYYY-MM-DDTHH:mm)
    const formatForInput = (timestamp: any) => {
      if (!timestamp) return '';
      if (typeof timestamp === 'string') return timestamp;
      try {
        const date = timestamp.toDate?.() || new Date(timestamp);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch {
        return '';
      }
    };
    
    setEditBookingOpenAt(formatForInput(selectedPeriod.booking_open_at));
    setEditBookingCloseAt(formatForInput(selectedPeriod.booking_close_at));
    setEditApiUrl(selectedPeriod.api_url);
    setEditApiUrl2(selectedPeriod.api_url2 || '');
    setEditSupplementationOpenAt(formatForInput(selectedPeriod.supplementation_open_at));
    setEditInfoText((selectedPeriod as any).infoText || '');
  };

  const handleSaveEdit = async () => {
    if (!selectedPeriod) return;

    try {
      // Muunna datetime-local merkkijonot Firestore Timestampeiksi
      const stringToTimestamp = (dateStr: string) => {
        if (!dateStr) return null;
        return Timestamp.fromDate(new Date(dateStr));
      };
      
      const periodRef = doc(db, 'booking_periods', selectedPeriod.id);
      await updateDoc(periodRef, {
        name: editName,
        mode: editMode,
        start_day: editStartDay,
        end_day: editEndDay,
        booking_open_at: stringToTimestamp(editBookingOpenAt),
        booking_close_at: stringToTimestamp(editBookingCloseAt),
        api_url: editApiUrl,
        api_url2: editApiUrl2,
        supplementation_open_at: editSupplementationOpenAt ? stringToTimestamp(editSupplementationOpenAt) : null,
        infoText: editInfoText,
      });

      setSuccess('Varausjakso päivitetty onnistuneesti!');
      setError('');
      setIsEditing(false);
      
      // Päivitä lokaalisti - käytä Timestamp-objekteja
      const updatedBookingOpenAt = stringToTimestamp(editBookingOpenAt);
      const updatedBookingCloseAt = stringToTimestamp(editBookingCloseAt);
      const updatedSupplementationOpenAt = editSupplementationOpenAt ? stringToTimestamp(editSupplementationOpenAt) : null;
      
      setBookingPeriods(prev => 
        prev.map(bp => bp.id === selectedPeriod.id 
          ? { ...bp, name: editName, mode: editMode, start_day: editStartDay, end_day: editEndDay, booking_open_at: updatedBookingOpenAt, booking_close_at: updatedBookingCloseAt, api_url: editApiUrl, api_url2: editApiUrl2, supplementation_open_at: updatedSupplementationOpenAt, infoText: editInfoText }
          : bp
        )
      );
      setSelectedPeriod({ ...selectedPeriod, name: editName, mode: editMode, start_day: editStartDay, end_day: editEndDay, booking_open_at: updatedBookingOpenAt, booking_close_at: updatedBookingCloseAt, api_url: editApiUrl, api_url2: editApiUrl2, supplementation_open_at: updatedSupplementationOpenAt, infoText: editInfoText } as any);
    } catch (err: any) {
      setError(`Virhe päivityksessä: ${err.message}`);
      setSuccess('');
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleToggleDelivered = async (reservationId: string, currentDelivered: boolean) => {
    try {
      const reservationRef = doc(db, 'reservations', reservationId);
      await updateDoc(reservationRef, {
        delivered: !currentDelivered
      });
      
      // Päivitä lokaali tila
      setReservations(prev => 
        prev.map(res => res.id === reservationId 
          ? { ...res, delivered: !currentDelivered }
          : res
        )
      );
    } catch (err: any) {
      setError(`Virhe toimitustilan päivityksessä: ${err.message}`);
    }
  };

  const filteredReservations = selectedPeriod 
    ? reservations.filter(res => res.booking_period_id === selectedPeriod.id)
    : [];

  const filteredLotteryWinners = selectedPeriod
    ? lotteryWinners.filter(winner => {
        // booking_period_id on Firestore DocumentReference, vertaa id:tä
        return winner.booking_period_id?.id === selectedPeriod.id;
      })
    : [];

  const displayData = selectedPeriod?.mode === 'LOTTERY' ? filteredLotteryWinners : filteredReservations;

  return (
    <div style={{ 
      backgroundColor: '#fff', 
      minHeight: '100vh', 
      padding: '2rem',
      color: '#000',
      maxWidth: '1200px',
      margin: '0 auto'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1 style={{ margin: 0 }}>Ylläpito</h1>
        <a 
          href="/" 
          style={{ 
            padding: '0.5rem 1rem', 
            backgroundColor: '#1976d2', 
            color: '#fff', 
            textDecoration: 'none', 
            borderRadius: '4px',
            fontSize: '0.9rem'
          }}
        >
          Varaussivulle
        </a>
      </div>
      {error && <div style={{ padding: '1rem', backgroundColor: '#ffebee', color: '#c62828', marginBottom: '1rem', borderRadius: '4px' }}>{error}</div>}
      {success && <div style={{ padding: '1rem', backgroundColor: '#e8f5e9', color: '#2e7d32', marginBottom: '1rem', borderRadius: '4px' }}>{success}</div>}
      
      {/* Lisää uusi booking period */}
      <section style={{ marginBottom: '3rem', padding: '1.5rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fafafa' }}>
        <div 
          onClick={() => setIsFormOpen(!isFormOpen)}
          style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            cursor: 'pointer',
            padding: '0.5rem'
          }}
        >
          <h2 style={{ margin: 0 }}>Lisää uusi varausjakso</h2>
          <span style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>{isFormOpen ? '−' : '+'}</span>
        </div>
        
        {isFormOpen && (
          <form onSubmit={handleAddBookingPeriod} style={{ display: 'grid', gap: '1rem', maxWidth: '600px', marginTop: '1.5rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Varausjakson nimi: <span style={{ fontWeight: 'normal', fontSize: '0.9rem', color: '#666' }}>(esim. Tammikuu)</span></label>
            <input 
              type="text" 
              value={newBookingPeriod.name} 
              onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, name: e.target.value })} 
              placeholder="Tammikuu"
              required 
              style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Jakson alkupäivä:</label>
              <input 
                type="date" 
                value={newBookingPeriod.start_day} 
                onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, start_day: e.target.value })} 
                required 
                style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Jakson loppupäivä:</label>
              <input 
                type="date" 
                value={newBookingPeriod.end_day} 
                onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, end_day: e.target.value })} 
                required 
                style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Varaus aukeaa:</label>
              <input 
                type="datetime-local" 
                value={newBookingPeriod.booking_open_at} 
                onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, booking_open_at: e.target.value })} 
                required 
                style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Varaus sulkeutuu:</label>
              <input 
                type="datetime-local" 
                value={newBookingPeriod.booking_close_at} 
                onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, booking_close_at: e.target.value })} 
                required 
                style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
              />
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Täydennysvaraus avautuu:</label>
            <input 
              type="datetime-local" 
              value={newBookingPeriod.supplementation_open_at} 
              onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, supplementation_open_at: e.target.value })} 
              style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Mode:</label>
            <select 
              value={newBookingPeriod.mode} 
              onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, mode: e.target.value })}
              style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value="FCFS">Nopeuskilpailu</option>
              <option value="LOTTERY">Arvonta</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
              API URL: <span style={{ fontWeight: 'normal', fontSize: '0.9rem', color: '#666' }}>(esim. https://www.liiga.fi/api/v2/schedule?tournament=<b>runkosarja</b>&season=2026 tai https://www.liiga.fi/api/v2/schedule?tournament=<b>playoffs</b>&season=2026)</span>
            </label>
            <input 
              type="url" 
              value={newBookingPeriod.api_url} 
              onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, api_url: e.target.value })} 
              placeholder="https://..."
              style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>API URL 2:</label>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#666', lineHeight: '1.4' }}>
              Tätä URL:ia tarvitaan, jos samaan aikaan menee kaksi eri tournamenttia, joihin jaetaan samalla jaksolla lippuja. 
              Esim. pudotuspelit ja playoutit voivat mennä samaan aikaan. Tällöin tähän tulisi esim. https://www.liiga.fi/api/v2/schedule?tournament=<b>playout</b>&season=2026 Muutoin voi jättää tyhjäksi.
            </p>
            <input 
              type="url" 
              value={newBookingPeriod.api_url2} 
              onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, api_url2: e.target.value })} 
              placeholder="https://..."
              style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>Infoteksti:</label>
            <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#666', lineHeight: '1.4' }}>
              Tämä teksti näkyy varaussivulla varausjakson tietojen alapuolella.
            </p>
            <textarea 
              value={newBookingPeriod.infoText} 
              onChange={(e) => setNewBookingPeriod({ ...newBookingPeriod, infoText: e.target.value })} 
              rows={5}
              style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', border: '1px solid #ccc', borderRadius: '4px', fontFamily: 'inherit', resize: 'vertical' }}
            />
          </div>
          <button 
            type="submit" 
            style={{ 
              padding: '0.75rem 1.5rem', 
              fontSize: '1rem', 
              backgroundColor: '#1976d2', 
              color: '#fff', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Lisää varausjakso
          </button>
        </form>
        )}
      </section>

      {/* Lista booking periodeista */}
      <section style={{ marginBottom: '3rem' }}>
        <h2>Varausjaksot</h2>
        {bookingPeriods.length === 0 ? (
          <p style={{ color: '#666' }}>Ei varausjaksoja</p>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            {[...bookingPeriods].sort((a, b) => {
              // Järjestä end_day mukaan, uusin (myöhäisin päivä) ylimmäksi
              return b.end_day.localeCompare(a.end_day);
            }).map((bp) => {
              const isSelected = selectedPeriod?.id === bp.id;
              const periodReservations = reservations.filter(res => res.booking_period_id === bp.id);
              const periodLotteryWinners = lotteryWinners.filter(winner => winner.booking_period_id?.id === bp.id);
              
              return (
                <div key={bp.id}>
                  <div 
                    onClick={() => setSelectedPeriod(isSelected ? null : bp)}
                    style={{ 
                      padding: '1rem', 
                      border: isSelected ? '2px solid #1976d2' : '1px solid #ddd',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: isSelected ? '#e3f2fd' : '#fafafa',
                      transition: 'all 0.2s'
                    }}
                  >
                    <h3 style={{ margin: '0 0 0.5rem 0' }}>{bp.name}</h3>
                    <p style={{ margin: '0.25rem 0', color: '#666' }}>
                      <strong>Mode:</strong> {bp.mode === 'FCFS' ? 'Nopeuskilpailu' : 'Arvonta'} | <strong>Päivät:</strong> {bp.start_day} - {bp.end_day}
                    </p>
                  </div>
                  
                  {/* Laajennettu sisältö heti tämän periodin alla */}
                  {isSelected && (
                    <div style={{ 
                      marginTop: '1rem', 
                      padding: '1.5rem', 
                      border: '2px solid #1976d2', 
                      borderRadius: '8px', 
                      backgroundColor: '#f5f5f5' 
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h3 style={{ margin: 0 }}>Varausjakson tiedot</h3>
                        {!isEditing && (
                          <Button 
                            variant="outlined" 
                            onClick={handleEditPeriod}
                            size="small"
                          >
                            Muokkaa
                          </Button>
                        )}
                      </div>
                      
                      {isEditing ? (
                        <div style={{ marginBottom: '2rem' }}>
                          <TextField
                            label="Nimi"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            fullWidth
                            margin="normal"
                          />
                          <FormControl fullWidth margin="normal">
                            <InputLabel>Mode</InputLabel>
                            <Select value={editMode} onChange={(e) => setEditMode(e.target.value)}>
                              <MenuItem value="FCFS">Nopeuskilpailu</MenuItem>
                              <MenuItem value="LOTTERY">Arvonta</MenuItem>
                            </Select>
                          </FormControl>
                          <TextField
                            label="Alkupäivä"
                            type="date"
                            value={editStartDay}
                            onChange={(e) => setEditStartDay(e.target.value)}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Loppupäivä"
                            type="date"
                            value={editEndDay}
                            onChange={(e) => setEditEndDay(e.target.value)}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Varaus aukeaa"
                            type="datetime-local"
                            value={editBookingOpenAt}
                            onChange={(e) => setEditBookingOpenAt(e.target.value)}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Varaus sulkeutuu"
                            type="datetime-local"
                            value={editBookingCloseAt}
                            onChange={(e) => setEditBookingCloseAt(e.target.value)}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="API URL"
                            value={editApiUrl}
                            onChange={(e) => setEditApiUrl(e.target.value)}
                            fullWidth
                            margin="normal"
                          />
                          <TextField
                            label="API URL 2"
                            value={editApiUrl2}
                            onChange={(e) => setEditApiUrl2(e.target.value)}
                            fullWidth
                            margin="normal"
                          />
                          <TextField
                            label="Täydennysvaraus avautuu"
                            type="datetime-local"
                            value={editSupplementationOpenAt}
                            onChange={(e) => setEditSupplementationOpenAt(e.target.value)}
                            fullWidth
                            margin="normal"
                            InputLabelProps={{ shrink: true }}
                          />
                          <TextField
                            label="Infoteksti"
                            value={editInfoText}
                            onChange={(e) => setEditInfoText(e.target.value)}
                            fullWidth
                            margin="normal"
                            multiline
                            rows={5}
                          />
                          <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                            <Button variant="contained" onClick={handleSaveEdit}>Tallenna</Button>
                            <Button variant="outlined" onClick={handleCancelEdit}>Peruuta</Button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ marginBottom: '2rem' }}>
                          <p><strong>ID:</strong> {bp.id}</p>
                          <p><strong>Nimi:</strong> {bp.name}</p>
                          <p><strong>Mode:</strong> {bp.mode}</p>
                          <p><strong>Alkupäivä:</strong> {bp.start_day}</p>
                          <p><strong>Loppupäivä:</strong> {bp.end_day}</p>
                          <p><strong>Varaus aukeaa:</strong> {bp.booking_open_at ? (typeof bp.booking_open_at === 'string' ? bp.booking_open_at : bp.booking_open_at.toDate?.().toLocaleString('fi-FI') || 'Virheellinen arvo') : 'Ei asetettu'}</p>
                          <p><strong>Varaus sulkeutuu:</strong> {bp.booking_close_at ? (typeof bp.booking_close_at === 'string' ? bp.booking_close_at : bp.booking_close_at.toDate?.().toLocaleString('fi-FI') || 'Virheellinen arvo') : 'Ei asetettu'}</p>
                          <p><strong>API URL:</strong> {bp.api_url}</p>
                          <p><strong>API URL 2:</strong> {bp.api_url2 || 'Ei asetettu'}</p>
                          <p><strong>Täydennysvaraus avautuu:</strong> {bp.supplementation_open_at ? (typeof bp.supplementation_open_at === 'string' ? bp.supplementation_open_at : bp.supplementation_open_at.toDate?.().toLocaleString('fi-FI') || 'Virheellinen arvo') : 'Ei asetettu'}</p>
                          <p><strong>Infoteksti:</strong></p>
                          <pre style={{ whiteSpace: 'pre-wrap', backgroundColor: '#f5f5f5', padding: '0.5rem', borderRadius: '4px', fontSize: '0.9rem' }}>{(bp as any).infoText || 'Ei asetettu'}</pre>
                        </div>
                      )}

                      {bp.mode?.toUpperCase() === 'LOTTERY' ? (
                        <>
                          <h3>Voittajat (WINNER: {periodLotteryWinners.length})</h3>
                          {periodLotteryWinners.length === 0 ? (
                            <p style={{ color: '#666' }}>Ei voittajia tälle varausjaksolle</p>
                          ) : (
                            <ul style={{ listStyle: 'none', padding: 0 }}>
                              {periodLotteryWinners
                                .sort((a, b) => {
                                  // Ensisijainen järjestys: homeTeam
                                  const homeCompare = (a.homeTeam || '').localeCompare(b.homeTeam || '');
                                  if (homeCompare !== 0) return homeCompare;
                                  // Toissijainen järjestys: päivämäärä
                                  return (a.day || '').localeCompare(b.day || '');
                                })
                                .map((winner) => {
                                  // Muotoile päivämäärä suomalaisittain
                                  const formatDate = (dateStr: string | null) => {
                                    if (!dateStr) return '';
                                    const date = new Date(dateStr);
                                    return date.toLocaleDateString('fi-FI');
                                  };
                                  
                                  // Taustaväri kotijoukkueen mukaan
                                  const getTeamColor = (team: string) => {
                                    const hash = team.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                                    const hue = hash % 360;
                                    return `hsl(${hue}, 70%, 95%)`;
                                  };
                                  
                                  return (
                                    <li 
                                      key={winner.id}
                                      style={{ 
                                        padding: '0.75rem', 
                                        marginBottom: '0.5rem', 
                                        backgroundColor: winner.homeTeam ? getTeamColor(winner.homeTeam) : '#fff', 
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <span>
                                        {formatDate(winner.day)} {winner.homeTeam || '?'} - {winner.awayTeam || '?'}, {winner.person_name || 'Ei nimeä'}
                                      </span>
                                      {winner.person_email && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                          <span style={{ color: '#666', fontSize: '0.9rem', userSelect: 'none' }}>
                                            {winner.person_email}
                                          </span>
                                          <ContentCopyIcon
                                            onClick={() => {
                                              if (winner.person_email) {
                                                navigator.clipboard.writeText(winner.person_email);
                                              }
                                            }}
                                            sx={{ 
                                              fontSize: '1rem',
                                              color: '#666',
                                              cursor: 'pointer',
                                              '&:hover': { color: '#333' }
                                            }}
                                          />
                                        </div>
                                      )}
                                    </li>
                                  );
                                })}
                            </ul>
                          )}
                        </>
                      ) : (
                        <>
                          <h3>Varaukset (RESERVED: {periodReservations.filter(r => r.status === 'RESERVED').length})</h3>
                          {periodReservations.filter(r => r.status === 'RESERVED').length === 0 ? (
                            <p style={{ color: '#666' }}>Ei RESERVED-varauksia tälle varausjaksolle</p>
                          ) : (
                            <ul style={{ listStyle: 'none', padding: 0 }}>
                              {periodReservations
                                .filter(r => r.status === 'RESERVED')
                                .sort((a, b) => {
                                  // Ensisijainen järjestys: homeTeam
                                  const homeCompare = (a.homeTeam || '').localeCompare(b.homeTeam || '');
                                  if (homeCompare !== 0) return homeCompare;
                                  // Toissijainen järjestys: päivämäärä
                                  return (a.day || '').localeCompare(b.day || '');
                                })
                                .map((res) => {
                                  // Muotoile päivämäärä suomalaisittain
                                  const formatDate = (dateStr: string | null) => {
                                    if (!dateStr) return '';
                                    const date = new Date(dateStr);
                                    return date.toLocaleDateString('fi-FI');
                                  };
                                  
                                  // Taustaväri kotijoukkueen mukaan
                                  const getTeamColor = (team: string) => {
                                    const hash = team.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
                                    const hue = hash % 360;
                                    return `hsl(${hue}, 70%, 95%)`;
                                  };
                                  
                                return (
                          <li 
                            key={res.id}
                            style={{ 
                              padding: '0.75rem', 
                              marginBottom: '0.5rem', 
                              backgroundColor: res.homeTeam ? getTeamColor(res.homeTeam) : '#fff', 
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <span>
                              {formatDate(res.day)} {res.homeTeam || '?'} - {res.awayTeam || '?'}, {res.person_name || 'Ei nimeä'}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                              {res.person_email && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ color: '#666', fontSize: '0.9rem', userSelect: 'none' }}>
                                    {res.person_email}
                                  </span>
                                  <ContentCopyIcon
                                    onClick={() => {
                                      if (res.person_email) {
                                        navigator.clipboard.writeText(res.person_email);
                                      }
                                    }}
                                    sx={{ 
                                      fontSize: '1rem',
                                      color: '#666',
                                      cursor: 'pointer',
                                      '&:hover': { color: '#333' }
                                    }}
                                  />
                                </div>
                              )}
                              <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', cursor: 'pointer', userSelect: 'none' }}>
                                <input
                                  type="checkbox"
                                  checked={res.delivered || false}
                                  onChange={() => handleToggleDelivered(res.id!, res.delivered || false)}
                                  style={{ cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '0.85rem', color: '#666' }}>Toimitettu</span>
                              </label>
                            </div>
                          </li>
                            );
                          })}
                        </ul>
                      )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default Admin;
